
import { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import ContactFooter from '../../components/feature/ContactFooter';

interface MisionHuellitas {
  id: string;
  title: string;
  description: string;
  city?: string;
  image_url?: string;
  image_url_2?: string;
  image_url_3?: string;
  image_url_4?: string;
  goal: number;
  raised: number;
  start_date: string;
  end_date: string;
  active: boolean;
  urgent: boolean;
  beneficiaries_count?: number;
  participation_link?: string;
  created_at: string;
}

export default function MisionHuellitas() {
  const [missions, setMissions] = useState<MisionHuellitas[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentImageIndices, setCurrentImageIndices] = useState<Record<string, number>>({});

  // Función para cambiar imagen de un card específico
  const changeImage = (itemId: string, direction: 'next' | 'prev', totalImages: number) => {
    setCurrentImageIndices(prev => {
      const currentIndex = prev[itemId] || 0;
      let newIndex;

      if (direction === 'next') {
        newIndex = currentIndex < totalImages - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : totalImages - 1;
      }

      return {
        ...prev,
        [itemId]: newIndex
      };
    });
  };

  // Función para obtener la imagen actual de un item
  const getCurrentImage = (itemId: string, images: string[]) => {
    const currentIndex = currentImageIndices[itemId] || 0;
    return images[currentIndex] || images[0];
  };

  // Función para verificar si hay sesión válida
  const checkSessionValidity = () => {
    const sessionData = localStorage.getItem('user_session');
    if (!sessionData) return false;

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();

      if (now > session.expiresAt) {
        localStorage.removeItem('user_session');
        return false;
      }

      return true;
    } catch (error) {
      localStorage.removeItem('user_session');
      return false;
    }
  };

  useEffect(() => {
    setIsLoggedIn(checkSessionValidity());
    loadMissions();
  }, []);

  const loadMissions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('mision_huellitas')
        .select('*')
        .eq('active', true)
        .order('urgent', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error cargando misiones:', error);
      } else {
        setMissions(data || []);
      }
    } catch (error) {
      console.error('Error cargando misiones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminClick = () => {
    if (isLoggedIn) {
      window.REACT_APP_NAVIGATE('/admin');
    } else {
      window.REACT_APP_NAVIGATE('/login');
    }
  };

  // Función para formatear números
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('es-MX').format(num);
  };

  // Función para calcular porcentaje
  const calculatePercentage = (raised: number, goal: number) => {
    return Math.min((raised / goal) * 100, 100);
  };

  // Función para formatear fechas
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div 
              className="flex items-center space-x-3 cursor-pointer mr-32"
              onClick={() => window.REACT_APP_NAVIGATE('/')}
            >
              <img
                src="/images/image.png"
                alt="Huellitas Unidas Logo"
                className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
              />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                  Huellitas Unidas
                </h1>
                <p className="text-xs sm:text-sm text-gray-600">Salvando vidas, una patita a la vez</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-purple-500 hover:bg-gray-50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-fill text-sm sm:text-base"></i>
                <span className="hidden sm:inline">Inicio</span>
              </button>
              <button
                onClick={handleAdminClick}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-gray-800 hover:bg-gray-50 whitespace-nowrap cursor-pointer"
              >
                <span className="text-sm sm:text-base">Iniciar sesión</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative text-white py-16 sm:py-20 overflow-hidden"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Inspiring%20animal%20rescue%20mission%2C%20volunteers%20helping%20dogs%2C%20community%20outreach%20program%2C%20heartwarming%20animal%20welfare%20campaign%2C%20professional%20charity%20photography%2C%20hope%20and%20transformation&width=1200&height=600&seq=mision-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-pink-900/80"></div>
        <div className="relative container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
            Misión Huellitas:
            <br />
            <span className="text-yellow-300">Proyectos que cambian vidas</span>
          </h1>
          <p className="text-base sm:text-xl md:text-2xl mb-6 sm:mb-8 max-w-4xl mx-auto opacity-90 leading-relaxed px-4 sm:px-0">
            Cada mes, Huellitas Unidas impulsa campañas con un propósito: salvar, sanar y transformar el destino de perritos que más lo necesitan. Aquí puedes conocer cada misión y decidir en cuál dejar tu huella.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <button
              onClick={() => {
                const missionsSection = document.querySelector('[data-missions-section]');
                if (missionsSection) {
                  missionsSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-purple-500 hover:bg-purple-60 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-heart-fill mr-2"></i>
              Ver Misiones
            </button>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="bg-white/20 hover:bg-white/30 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-300 backdrop-blur-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Volver al Inicio
            </button>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-heart-fill text-2xl sm:text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                {missions.length}
              </h3>
              <p className="text-sm sm:text-base text-gray-600">Misiones Activas</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-team-fill text-2xl sm:text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                {missions.reduce((total, mission) => total + (mission.beneficiaries_count || 0), 0)}
              </h3>
              <p className="text-sm sm:text-base text-gray-600">Perritos Beneficiados</p>
            </div>
          </div>
        </div>
      </section>

      {/* Missions Section */}
      <section className="py-8 sm:py-12 bg-gray-50" data-missions-section>
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">Misiones Activas</h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Únete a nuestras misiones activas y ayuda a cambiar la vida de perritos necesitados
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16 sm:py-20">
              <div className="text-center">
                <i className="ri-loader-4-line text-3xl sm:text-4xl text-purple-500 animate-spin mb-4"></i>
                <p className="text-sm sm:text-base text-gray-600">Cargando misiones...</p>
              </div>
            </div>
          ) : missions.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <i className="ri-heart-line text-5xl sm:text-6xl text-gray-300 mb-4"></i>
              <p className="text-base sm:text-lg text-gray-500 px-4 sm:px-8">
                No hay misiones activas en este momento
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-product-shop>
              {missions.map((mission) => {
                const images = [
                  mission.image_url,
                  mission.image_url_2,
                  mission.image_url_3,
                  mission.image_url_4
                ].filter((url): url is string => Boolean(url));

                const hasMultipleImages = images.length > 1;
                const currentImage = getCurrentImage(mission.id, images);
                const currentIndex = currentImageIndices[mission.id] || 0;

                return (
                  <div
                    key={mission.id}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                  >
                    {/* Etiqueta de urgente */}
                    <div className="flex justify-between p-3 sm:p-4 pb-0">
                      {mission.urgent && (
                        <div className="bg-red-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-semibold">
                          <i className="ri-alarm-warning-fill mr-1"></i>
                          URGENTE
                        </div>
                      )}
                      <div className="bg-green-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ml-auto">
                        ACTIVA
                      </div>
                    </div>

                    {/* Imagen con navegación */}
                    <div className="relative mx-3 sm:mx-4 mb-3 sm:mb-4">
                      <img
                        src={currentImage || `https://readdy.ai/api/search-image?query=Animal%20rescue%20mission%20project%2C%20dogs%20being%20helped%20and%20cared%20for%2C%20veterinary%20treatment%2C%20community%20support%20for%20animals%2C%20heartwarming%20rescue%20story%2C%20professional%20charity%20photography&width=400&height=300&seq=mission-${mission.id}&orientation=landscape`}
                        alt={mission.title}
                        className="w-full h-40 sm:h-48 object-cover object-top rounded-xl"
                      />

                      {/* Contador de imágenes */}
                      {hasMultipleImages && (
                        <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                          <i className="ri-image-line mr-1"></i>
                          {currentIndex + 1}/{images.length}
                        </div>
                      )}

                      {/* Botones de navegación */}
                      {hasMultipleImages && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              changeImage(mission.id, 'prev', images.length);
                            }}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full transition-all duration-200"
                          >
                            <i className="ri-arrow-left-line text-sm"></i>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              changeImage(mission.id, 'next', images.length);
                            }}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full transition-all duration-200"
                          >
                            <i className="ri-arrow-right-line text-sm"></i>
                          </button>
                        </>
                      )}
                    </div>

                    <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">{mission.title}</h3>
                      <p className="text-gray-600 mb-3 sm:mb-4 text-sm leading-relaxed">{mission.description}</p>

                      {/* Información adicional */}
                      <div className="space-y-2 mb-3 sm:mb-4">
                        {mission.city && (
                          <div className="flex items-center text-xs sm:text-sm text-gray-600">
                            <i className="ri-map-pin-fill mr-2 text-red-500"></i>
                            <span>{mission.city}</span>
                          </div>
                        )}
                        {mission.beneficiaries_count && (
                          <div className="flex items-center text-xs sm:text-sm text-gray-600">
                            <i className="ri-team-fill mr-2 text-blue-500"></i>
                            <span>{mission.beneficiaries_count} perritos beneficiados</span>
                          </div>
                        )}
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                          <i className="ri-calendar-fill mr-2 text-green-500"></i>
                          <span>{formatDate(mission.start_date)} - {mission.end_date ? formatDate(mission.end_date) : 'Fecha abierta'}</span>
                        </div>
                      </div>

                      {/* Progreso de recaudación */}
                      <div className="mb-3 sm:mb-4">
                        <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-2">
                          <span>Recaudado: ${formatNumber(mission.raised || 0)}</span>
                          <span>Meta: ${formatNumber(mission.goal || 0)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                          <div
                            className="bg-gradient-to-r from-purple-400 to-pink-600 h-2 sm:h-3 rounded-full transition-all duration-500"
                            style={{ width: `${calculatePercentage(mission.raised || 0, mission.goal || 1)}%` }}
                          ></div>
                        </div>
                        <div className="text-center mt-2">
                          <span className="text-purple-600 font-semibold text-xs sm:text-sm">
                            {Math.round(calculatePercentage(mission.raised || 0, mission.goal || 1))}% completado
                          </span>
                        </div>
                      </div>

                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (mission.participation_link) {
                            window.open(mission.participation_link, '_blank');
                          } else {
                            alert('Enlace de participación no disponible');
                          }
                        }}
                        className="w-full py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base bg-purple-500 hover:bg-purple-600 text-white"
                      >
                        <i className="ri-heart-fill mr-2"></i>
                        Participar en la Misión
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-4xl font-bold mb-4 sm:mb-6">¿Quieres ser parte del cambio?</h2>
          <p className="text-base sm:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto opacity-90 px-4 sm:px-0">
            Cada donación, cada adopción, cada gesto de amor marca la diferencia en la vida de un perrito necesitado
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <button
              onClick={() => window.open('https://link.mercadopago.com.mx/huellitasunidas', '_blank')}
              className="bg-white text-purple-600 hover:bg-gray-100 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-heart-fill mr-2"></i>
              Hacer Donación
            </button>
            <button
              onClick={() => {
                window.REACT_APP_NAVIGATE('/');
                // Esperar a que la página cargue y luego activar la sección de adoptar
                setTimeout(() => {
                  // Disparar evento personalizado para cambiar a la pestaña de adoptar
                  const event = new CustomEvent('tabChange', { detail: 'adoptar' });
                  window.dispatchEvent(event);
                }, 100);
              }}
              className="bg-white/20 hover:bg-white/30 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-300 backdrop-blur-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-heart-fill mr-2"></i>
              Ver Adopciones
            </button>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <ContactFooter />
    </div>
  );
}
